import { VariantProps, cva } from 'class-variance-authority'
import { XIcon } from 'lucide-react'
import { useLayoutEffect, useMemo, useRef, useState } from 'react'

import { cn } from '@/lib/ui/style-helpers'

import { Tooltip } from '@/components/lemmala-design-system/tooltip/tooltip'

import { FittedLine, fitTagWidths, tagListAvailableWidth } from './tag-fit'

const tagBadgeVariants = cva(
  'inline-flex h-6 max-w-full min-w-0 items-center gap-1.5 overflow-hidden rounded-full px-2 py-1 !text-label-small',
  {
    variants: {
      variant: {
        default: 'bg-main-background-interactive-secondary-default text-main-foreground-secondary',
        promotion: 'bg-main-background-subtle-semantic-accent text-main-foreground-accent',
        success: 'bg-main-background-subtle-semantic-success text-main-foreground-success',
        ghost: 'bg-transparent rounded-none px-0 text-main-foreground-primary !text-label-small',
      },
      size: {
        large: 'h-8',
        default: 'h-6',
      },
    },
    defaultVariants: {
      size: 'default',
      variant: 'default',
    },
  },
)

type TagBadgeProps = {
  tag?: Tag
  onRemove?: (e: React.MouseEvent<HTMLButtonElement>) => void
  onClick?: (e: React.MouseEvent<HTMLDivElement>) => void
  showDot?: boolean
  disabled?: boolean
  className?: string
  rootClassName?: string
  tooltip?: string
  maxWidth?: number
}

export const TagBadge = ({
  tag,
  onRemove,
  showDot = false,
  disabled = false,
  className,
  rootClassName,
  onClick,
  tooltip,
  maxWidth,
}: TagBadgeProps) => {
  if (!tag) return null

  const badgeStyle = cn(tagBadgeVariants({ variant: tag.variant, size: tag.size }), rootClassName)

  return (
    <div
      className={badgeStyle}
      style={maxWidth != null ? { width: maxWidth, maxWidth } : undefined}
      onClick={onClick}
      data-testid={`tag-badge-${tag.id}`}
    >
      {showDot && (
        <div
          className='size-2 rounded-full bg-main-foreground-danger'
          data-testid={`tag-dot-${tag.id}`}
        />
      )}
      {tag.icon}
      <Tooltip
        tooltipDisabled={!tooltip}
        triggerClassName={cn('min-w-0 max-w-full overflow-hidden', className)}
        trigger={
          <span
            className={cn('block min-w-0 max-w-64 truncate', {
              'cursor-pointer hover:underline': onClick,
            })}
            data-testid={`tag-name-${tag.id}`}
          >
            {tag.name}
          </span>
        }
      >
        {tooltip}
      </Tooltip>

      {onRemove && (
        <span
          onClick={onRemove}
          className={`flex size-4 shrink-0 cursor-pointer items-center justify-center ${
            disabled
              ? 'text-main-foreground-disabled'
              : 'text-main-foreground-tertiary hover:text-main-foreground-primary'
          }`}
          data-testid={`tag-remove-${tag.id}`}
        >
          <XIcon className='size-4' />
        </span>
      )}
    </div>
  )
}

export type Tag = {
  id: string
  name: string
  icon?: React.ReactNode
  onClick?: (e: React.MouseEvent<HTMLDivElement>) => void
  tooltip?: string
} & VariantProps<typeof tagBadgeVariants>

function sameFit(current: FittedLine[] | null, next: FittedLine[] | null) {
  if (current === next) return true
  if (!current || !next || current.length !== next.length) return false
  return current.every(
    (line, index) =>
      line.start === next[index].start &&
      line.count === next[index].count &&
      line.truncateWidth === next[index].truncateWidth,
  )
}

const tagsListVariants = cva('flex items-center', {
  variants: {
    variant: {
      default: '',
      ghost: '',
    },
    wrap: {
      true: 'flex-wrap gap-1',
      false: 'min-w-0 items-center gap-1',
    },
  },
  defaultVariants: {
    variant: 'default',
    wrap: true,
  },
})

type TagsListProps = {
  tags: Tag[]
  maxDisplay?: number
  maxLines?: number
  onRemove?: (e: React.MouseEvent<HTMLButtonElement>, tagId: string) => void
  disabled?: boolean
  showDot?: boolean
  className?: string
  tagClassName?: string
  tooltipClassName?: string
  // Preserves the caller's tag order. Defaults to true, which sorts by name.
  sort?: boolean
} & VariantProps<typeof tagsListVariants>

export const TagsList = ({
  tags,
  maxDisplay,
  maxLines = 1,
  onRemove,
  disabled = false,
  showDot = false,
  wrap = true,
  className,
  tagClassName,
  tooltipClassName,
  variant = 'default',
  sort = true,
}: TagsListProps) => {
  const containerRef = useRef<HTMLDivElement>(null)
  const measureRef = useRef<HTMLDivElement>(null)
  const [fitted, setFitted] = useState<FittedLine[] | null>(null)

  const orderedTags = useMemo(() => {
    const present = tags.filter(Boolean)
    return sort ? [...present].sort((a, b) => a.name.localeCompare(b.name)) : present
  }, [tags, sort])

  const displayLimit = maxDisplay ?? (wrap ? 3 : orderedTags.length)
  const hasRemove = onRemove != null

  useLayoutEffect(() => {
    if (wrap) return undefined

    const container = containerRef.current
    const measure = measureRef.current
    if (!container || !measure) return undefined

    const update = () => {
      const available = tagListAvailableWidth(container)
      if (available <= 0) {
        setFitted((current) => (current === null ? current : null))
        return
      }

      const widths = [...measure.querySelectorAll<HTMLElement>('[data-fit-badge]')].map(
        (badge) => badge.getBoundingClientRect().width,
      )
      const chipWidth =
        measure.querySelector<HTMLElement>('[data-fit-chip]')?.getBoundingClientRect().width ?? 0
      const separatorWidth =
        measure.querySelector<HTMLElement>('[data-fit-sep]')?.getBoundingClientRect().width ?? 0
      const gap = Number.parseFloat(getComputedStyle(measure).columnGap) || 0
      const next = fitTagWidths({
        widths,
        available,
        gap,
        chipWidth,
        separatorWidth,
        maxDisplay: displayLimit,
        maxLines,
      })
      setFitted((current) => (sameFit(current, next) ? current : next))
    }

    update()
    const observer = new ResizeObserver(update)
    observer.observe(container)
    observer.observe(measure)
    if (container.parentElement) observer.observe(container.parentElement)
    return () => observer.disconnect()
  }, [
    wrap,
    orderedTags,
    displayLimit,
    maxLines,
    variant,
    showDot,
    disabled,
    tagClassName,
    hasRemove,
  ])

  const renderBadges = (lineTags: Tag[], truncateWidth: number | null) => {
    const badges = lineTags.map((tag, index) => (
      <TagBadge
        key={tag.id}
        tag={variant === 'ghost' ? { ...tag, variant: 'ghost' } : tag}
        onRemove={onRemove && ((e) => onRemove(e, tag.id))}
        disabled={disabled}
        showDot={showDot}
        className={tagClassName}
        rootClassName='shrink-0'
        onClick={tag.onClick}
        tooltip={tag.tooltip}
        maxWidth={
          truncateWidth != null && index === lineTags.length - 1 ? truncateWidth : undefined
        }
      />
    ))

    if (variant !== 'ghost') return badges

    return badges.flatMap((badge, index) =>
      index === 0
        ? [badge]
        : [
            <span
              key={`tag-sep-${lineTags[index]?.id ?? index}`}
              aria-hidden
              className='text-main-foreground-placeholder text-label-small'
            >
              ・
            </span>,
            badge,
          ],
    )
  }

  const overflowFor = (hidden: Tag[]) =>
    hidden.length > 0 && (
      <Tooltip
        className={cn('max-w-fit', tooltipClassName)}
        triggerClassName='shrink-0'
        trigger={
          <TagBadge rootClassName='shrink-0' tag={{ id: 'overflow', name: `+${hidden.length}` }} />
        }
      >
        <div className='grid max-h-64 grid-cols-1 gap-2 overflow-y-auto p-1'>
          {hidden.map((tag) => (
            <TagBadge key={tag.id} tag={tag} onClick={tag.onClick} />
          ))}
        </div>
      </Tooltip>
    )

  const fittedLines =
    fitted ??
    (orderedTags.length === 0
      ? []
      : [
          {
            start: 0,
            count: Math.min(displayLimit, orderedTags.length),
            truncateWidth: null,
          },
        ])
  const fittedVisibleCount = fittedLines.reduce((sum, line) => sum + line.count, 0)
  const fittedHidden = orderedTags.slice(fittedVisibleCount)
  const overflowIndicator = overflowFor(fittedHidden)

  if (!wrap) {
    return (
      <div
        ref={containerRef}
        className={cn('relative', tagsListVariants({ variant, wrap: false }), className)}
        data-testid='tags-list'
      >
        <div
          ref={measureRef}
          aria-hidden
          className='pointer-events-none invisible absolute flex gap-1'
        >
          {orderedTags.map((tag) => (
            <div key={tag.id} data-fit-badge>
              <TagBadge
                tag={variant === 'ghost' ? { ...tag, variant: 'ghost' } : tag}
                onRemove={onRemove && ((e) => onRemove(e, tag.id))}
                disabled={disabled}
                showDot={showDot}
                className={tagClassName}
                rootClassName='shrink-0'
              />
            </div>
          ))}
          {variant === 'ghost' && (
            <span data-fit-sep className='text-main-foreground-placeholder text-label-small'>
              ・
            </span>
          )}
          <div data-fit-chip>
            <TagBadge
              rootClassName='shrink-0'
              tag={{ id: 'measure-overflow', name: `+${Math.max(orderedTags.length, 1)}` }}
            />
          </div>
        </div>
        {fittedLines.length === 0 ? (
          overflowIndicator
        ) : (
          <div className='inline-flex w-fit min-w-0 max-w-full flex-col gap-1'>
            {fittedLines.map((line, lineIndex) => (
              <div
                key={line.start}
                className='inline-flex w-fit min-w-0 max-w-full items-center gap-1'
              >
                {renderBadges(
                  orderedTags.slice(line.start, line.start + line.count),
                  line.truncateWidth,
                )}
                {lineIndex === fittedLines.length - 1 && overflowIndicator}
              </div>
            ))}
          </div>
        )}
      </div>
    )
  }

  const wrappedTags = orderedTags.slice(0, displayLimit)
  const wrappedHidden = orderedTags.slice(displayLimit)

  return (
    <div
      className={cn(tagsListVariants({ variant, wrap: true }), className)}
      data-testid='tags-list'
    >
      {renderBadges(wrappedTags, null)}
      {overflowFor(wrappedHidden)}
    </div>
  )
}
